import { router } from '@inertiajs/react'
import {
  Badge,
  Button,
  Card,
  Group,
  PasswordInput,
  Stack,
  Switch,
  TextInput,
  Title,
} from '@mantine/core'
import { type FormEvent, useCallback, useState } from 'react'
import { groupByPrefix, labelForKey } from '@/lib/settings-utils'
import type { SettingDefinition } from '@/types'

function placeholderForDefn(
  defn: SettingDefinition,
  sensitiveConfigured: Record<string, boolean>,
  orgSettings?: Record<string, unknown>,
  orgSensitiveConfigured?: Record<string, boolean>,
): string {
  if (orgSettings) {
    if (defn.sensitive) {
      const orgConfigured = orgSensitiveConfigured?.[defn.key]
      return orgConfigured ? 'Inherited from org (configured)' : 'Not configured at org level'
    }
    const orgVal = orgSettings[defn.key]
    if (orgVal != null) return `Inherited from org: ${orgVal}`
    if (defn.default != null) return `Default: ${defn.default}`
    return 'Not set'
  }

  if (defn.sensitive) {
    return sensitiveConfigured[defn.key] ? 'Configured (leave blank to keep)' : 'Not configured'
  }
  return defn.default != null ? `Default: ${defn.default}` : 'Not set'
}

export default function SettingsForm({
  registry,
  settings,
  sensitiveConfigured,
  submitUrl,
  submitLabel = 'Save Settings',
  orgSettings,
  orgSensitiveConfigured,
}: {
  registry: SettingDefinition[]
  settings: Record<string, unknown>
  sensitiveConfigured: Record<string, boolean>
  submitUrl: string
  submitLabel?: string
  orgSettings?: Record<string, unknown>
  orgSensitiveConfigured?: Record<string, boolean>
}) {
  const [formData, setFormData] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {}
    for (const defn of registry) {
      if (defn.sensitive) {
        initial[defn.key] = ''
      } else {
        const val = settings[defn.key]
        initial[defn.key] = val != null ? String(val) : ''
      }
    }
    return initial
  })
  const [saving, setSaving] = useState(false)

  const handleSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault()
      setSaving(true)

      const payload: Record<string, string> = {}
      for (const defn of registry) {
        const val = formData[defn.key]
        if (defn.sensitive && val === '') continue
        payload[defn.key] = val
      }

      router.patch(submitUrl, { settings: payload }, {
        preserveScroll: true,
        onFinish: () => setSaving(false),
      })
    },
    [formData, registry, submitUrl],
  )

  const groups = groupByPrefix(registry)

  return (
    <form onSubmit={handleSubmit}>
      <Stack gap="lg">
        {Object.entries(groups).map(([prefix, defs]) => (
          <Card key={prefix} withBorder>
            <Title order={5} mb="sm" tt="capitalize">{prefix}</Title>
            <Stack gap="sm">
              {defs.map((defn) => {
                const placeholder = placeholderForDefn(defn, sensitiveConfigured, orgSettings, orgSensitiveConfigured)

                if (defn.type === 'boolean') {
                  const hasOverride = orgSettings && settings[defn.key] !== orgSettings[defn.key] && formData[defn.key] !== ''
                  return (
                    <Group key={defn.key} gap="xs">
                      <Switch
                        label={labelForKey(defn.key)}
                        checked={formData[defn.key] === '' ? (settings[defn.key] as boolean) ?? (defn.default as boolean) ?? false : formData[defn.key] === 'true'}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, [defn.key]: e.currentTarget.checked ? 'true' : 'false' }))
                        }
                      />
                      {hasOverride && (
                        <Badge variant="light" size="xs" color="blue">Override</Badge>
                      )}
                    </Group>
                  )
                }

                if (defn.sensitive) {
                  const sensitivePlaceholder = orgSettings
                    ? (sensitiveConfigured[defn.key] ? 'Project override set (leave blank to keep)' : placeholder)
                    : placeholder
                  return (
                    <PasswordInput
                      key={defn.key}
                      label={labelForKey(defn.key)}
                      placeholder={sensitivePlaceholder}
                      value={formData[defn.key]}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, [defn.key]: e.currentTarget.value }))
                      }
                    />
                  )
                }

                return (
                  <TextInput
                    key={defn.key}
                    label={labelForKey(defn.key)}
                    placeholder={placeholder}
                    value={formData[defn.key]}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, [defn.key]: e.currentTarget.value }))
                    }
                  />
                )
              })}
            </Stack>
          </Card>
        ))}

        <Group>
          <Button type="submit" loading={saving}>
            {submitLabel}
          </Button>
        </Group>
      </Stack>
    </form>
  )
}
